import type {
  FeedbackCapabilities,
  FeedbackResponse,
  PublicReviewDestination,
  SubmitFeedback
} from '@/types/feedback'

const TOKEN = /^[A-Za-z0-9_-]{43}$/
export const isFeedbackPath = (path: string): boolean => /^\/feedback\/?$/.test(path)

// No storage, router, Auth0, analytics or account-backed imports in this module.
export function captureFeedbackCapabilities(): FeedbackCapabilities {
  const url = new URL(window.location.href)
  const fragment = new URLSearchParams(url.hash.slice(1))
  const read = (key: string): string | null => {
    const values = fragment.getAll(key)
    return values.length === 1 && TOKEN.test(values[0]) ? values[0] : null
  }
  const capabilities = isFeedbackPath(url.pathname)
    ? { review: read('token'), stop: read('stop') }
    : { review: null, stop: null }
  if (isFeedbackPath(url.pathname)) {
    // Query parameters never supply identity or credentials, even on malformed links.
    window.history.replaceState(null, '', '/feedback')
  }
  return capabilities
}

export async function openNativeFeedbackUrl(value: string): Promise<boolean> {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  const feedback =
    isFeedbackPath(url.pathname) ||
    (url.protocol === 'com.miinta.app:' && url.hostname === 'feedback')
  if (!feedback) return false
  // Reject lookalikes/custom schemes before the legacy router can drop the fragment.
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.port ||
    !['app.hellomiinta.com', 'app.dev.hellomiinta.com'].includes(url.hostname)
  )
    return true
  url.search = ''
  try {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url: url.href })
  } catch {
    // Never log the URL, plugin error or capability. Reopen the original link to retry.
    console.error('[Feedback] Could not open the private web form')
  }
  return true
}

type FeedbackResult = { ok: true; data: FeedbackResponse } | { ok: false; status: number }

export async function requestFeedback(
  path: string,
  token: string,
  body?: SubmitFeedback | null,
  signal?: AbortSignal
): Promise<FeedbackResult> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  if (signal?.aborted) abort()
  signal?.addEventListener('abort', abort, { once: true })
  const timeout = window.setTimeout(abort, 20_000)
  try {
    const response = await fetch(`${import.meta.env.VITE_API_ROOT.replace(/\/$/, '')}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'omit',
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      signal: controller.signal
    })
    if (!response.ok) return { ok: false, status: response.status }
    const data: FeedbackResponse = await response.json()
    if (!['available', 'completed', 'expired', 'stopped', 'unavailable'].includes(data?.state)) {
      return { ok: false, status: 0 }
    }
    return { ok: true, data }
  } catch {
    // No automatic retry: POST may already have committed. Do not retain raw errors.
    return { ok: false, status: 0 }
  } finally {
    window.clearTimeout(timeout)
    signal?.removeEventListener('abort', abort)
  }
}

// Core chooses the destination. This is only a defensive external-navigation boundary.
export function safePublicReview(
  value: PublicReviewDestination | null | undefined
): PublicReviewDestination | null {
  if (
    !value ||
    typeof value.url !== 'string' ||
    value.url.length > 2048 ||
    /[\s\\]/.test(value.url) ||
    [...value.url].some((char) => char.charCodeAt(0) < 33 || char.charCodeAt(0) === 127)
  )
    return null
  try {
    const url = new URL(value.url)
    if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash)
      return null
    const valid =
      value.platform === 'doctoralia'
        ? url.hostname === 'www.doctoralia.es' &&
          !url.search &&
          /^\/anade-opinion\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)
        : value.platform === 'google' &&
          ((url.hostname === 'g.page' &&
            !url.search &&
            /^\/r\/[A-Za-z0-9_-]+\/review\/?$/.test(url.pathname)) ||
            (url.hostname === 'search.google.com' &&
              url.pathname === '/local/writereview' &&
              Array.from(url.searchParams.keys()).join(',') === 'placeid' &&
              /^[A-Za-z0-9_-]+$/.test(url.searchParams.get('placeid') ?? '')))
    return valid ? { platform: value.platform, url: url.href } : null
  } catch {
    return null
  }
}
